import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { TabNames } from "../Assets/variables";
import { parseApiSchemas } from "../Utils/contentTransformers";
import openApiSpec from "../Assets/API/V3/streamingMarketData.json";
import APIContentItem from "./APIContentItem";
import MiniSearch from "minisearch";
import { Marker } from "react-mark.js";

const index = new MiniSearch({
    fields: ['name', 'apiFieldName', 'description', 'enumValues'],
    storeFields: ['name', 'apiFieldName', 'description', 'type', 'format', 'enumValues'],
    searchOptions: {
        combineWith: "AND",
        boost: {name: 3, apiFieldName: 2, description: 2, enumValues: 1},
        prefix: true,
        fuzzy: false
    }
});

const specification = parseApiSchemas(openApiSpec, "intradaySnapshot");

function createAllFields() {
    const fieldsSet = new Set();
    const initAllFields = [];

    Object.keys(specification).forEach((messageTitle) => {
        Object.keys(specification[messageTitle]).forEach((apiField) => {
            const fieldKey = `streamingMarketDataV3_${apiField}`;
            const property = specification[messageTitle][apiField];

            if (!fieldsSet.has(fieldKey) && property.description !== undefined) {
                const field = {
                    id: initAllFields.length,
                    name: apiField,
                    apiFieldName: property.fieldName,
                    description: property.description ? property.description : '',
                    type: property.type ? property.type : '',
                    format: property.format ? property.format : '',
                    enumValues: property.enum ? property.enum : []
                };

                initAllFields.push(field);
                fieldsSet.add(fieldKey);
            }
        });
    });

    initAllFields.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });

    return initAllFields;
}

const APIContentStreamingMarketDataV3 = () => {
    const { setActiveTabItem } = useOutletContext();
    const searchInputElementRef = useRef('');
    const searchClearButtonElementRef = useRef(null);
    const resultListElementRef = useRef(null);
    const [results, setResults] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const allFieldsRef = useRef(createAllFields());
    const [allFields] = useState(allFieldsRef.current);

    useEffect(() => {
        setActiveTabItem(TabNames.Content);
        index.removeAll();
        index.addAll(allFieldsRef.current);
    }, []);

    const handleSearch = () => {
        const newResults = index.search(searchInputElementRef.current.value, {});
        setInputValue(searchInputElementRef.current.value);
        setResults(newResults);

        if (searchInputElementRef.current.value.length > 0) {
            searchClearButtonElementRef.current.classList.add("tab-content__field-searchButton--shown");
            searchClearButtonElementRef.current.classList.remove("tab-content__field-searchButton--hidden");
        } else {
            searchClearButtonElementRef.current.classList.add("tab-content__field-searchButton--hidden");
            searchClearButtonElementRef.current.classList.remove("tab-content__field-searchButton--shown");
        }
    };

    // Clicking on clear button clears search
    const clearSearchField = () => {
        searchInputElementRef.current.value = '';
        searchInputElementRef.current.focus();
        searchClearButtonElementRef.current.classList.add("tab-content__field-searchButton--hidden");
        searchClearButtonElementRef.current.classList.remove("tab-content__field-searchButton--shown");
        setInputValue('');
        setResults(allFields);
    };

    return (
        <div id="tab-content" className="api-panel">
            <header className="content-tab-header">
                <div className="content-tab-header__inner">
                    <div className="content-tab-header__searchbar">
                        <div className="searchbar-container">
                            <div id="search">
                                <input
                                    id="searchbar"
                                    type="text"
                                    name="search"
                                    placeholder="Search"
                                    autoComplete="none"
                                    autoCorrect="none"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    ref={searchInputElementRef}
                                    value={inputValue}
                                    onChange={() => handleSearch()} />
                                <button
                                    className="tab-content__field-searchButton tab-content__field-searchButton--hidden"
                                    id="clear"
                                    ref={searchClearButtonElementRef}
                                    onClick={() => clearSearchField()}>×</button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <div id="result-list" ref={resultListElementRef}>
                <Marker mark={searchInputElementRef.current.value}>
                    {searchInputElementRef.current.value
                        ? results.map((result, index) => <APIContentItem result={result} key={index} />)
                        : allFields.map((result, index) => (
                            <APIContentItem result={result} key={index} />
                    ))}
                </Marker>
            </div>
        </div>
    );
};

export default APIContentStreamingMarketDataV3;
